import type { AuthSession } from "@lodestone/hungry-lion-procurement-client";
import {
  LogtoProvider,
  UserScope,
  useHandleSignInCallback,
  useLogto,
} from "@logto/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

import { shouldCompleteAuthCallback } from "./auth-callback";
import { createLogtoSignInOptions } from "./logto-sign-in";

const productionLogto = {
  endpoint: "https://avjm2m.logto.app",
  applicationId: "v0sg92dspwwhz69qe8s5p",
  apiResource:
    "https://lodestone-hl-prototype-gateway.lodestone-tech.workers.dev/api",
  organizationId: "5cy8i018qmkb",
};
const logtoConfiguration = {
  endpoint: import.meta.env.VITE_LOGTO_ENDPOINT ?? productionLogto.endpoint,
  applicationId:
    import.meta.env.VITE_LOGTO_APPLICATION_ID ?? productionLogto.applicationId,
  apiResource:
    import.meta.env.VITE_LOGTO_API_RESOURCE ?? productionLogto.apiResource,
  organizationId:
    import.meta.env.VITE_LOGTO_ORGANIZATION_ID ??
    productionLogto.organizationId,
  callbackUri: import.meta.env.VITE_LOGTO_CALLBACK_URI,
};

export const apiResource = logtoConfiguration.apiResource;
export const organizationId = logtoConfiguration.organizationId;

const LogtoAuthSessionProvider = ({ children }: { children: ReactNode }) => (
  <LogtoProvider
    config={{
      endpoint: logtoConfiguration.endpoint,
      appId: logtoConfiguration.applicationId,
      resources: [apiResource],
      scopes: [UserScope.Organizations, "lodestone:access"],
    }}
  >
    {children}
  </LogtoProvider>
);

const useLogtoAuthSession = (): {
  session: AuthSession;
  isAuthenticated: boolean;
  isLoading: boolean;
} => {
  const logto = useLogto();
  const session = useMemo<AuthSession>(
    () => ({
      async signIn() {
        await logto.signIn(
          createLogtoSignInOptions(
            logtoConfiguration.callbackUri ?? `${location.origin}/callback`,
          ),
        );
      },
      async signOut(returnTo) {
        await logto.signOut(returnTo);
      },
      async getActor() {
        const user = await logto.fetchUserInfo();
        if (!user) return undefined;
        return {
          actorId: user.sub,
          displayName: user.name ?? user.username ?? user.sub,
        };
      },
      listTenantContexts() {
        return Promise.resolve([
          {
            organizationId,
            displayName: "Hungry Lion",
          },
        ]);
      },
      selectTenant(candidate) {
        if (candidate !== organizationId) {
          return Promise.reject(
            new Error("The selected organization is not available"),
          );
        }
        return Promise.resolve();
      },
      async getApiAccessToken() {
        const token = await logto.getAccessToken(apiResource, organizationId);
        if (!token) {
          throw new Error("No organization access token is available");
        }
        return token;
      },
    }),
    [logto],
  );

  return {
    session,
    isAuthenticated: logto.isAuthenticated,
    isLoading: logto.isLoading,
  };
};

type AuthSessionCallbackState = { error?: Error; isLoading: boolean };
const useLogtoAuthSessionCallback = (
  onComplete: () => void,
): AuthSessionCallbackState => {
  const completed = useRef(false);
  const completeOnce = useCallback(() => {
    if (completed.current) return;
    completed.current = true;
    onComplete();
  }, [onComplete]);
  const state = useHandleSignInCallback(completeOnce);

  useEffect(() => {
    if (shouldCompleteAuthCallback(state)) completeOnce();
  }, [completeOnce, state]);

  return {
    isLoading: state.isLoading,
    ...(state.error instanceof Error ? { error: state.error } : {}),
  };
};

const acceptanceTokenKey = "lodestone.acceptance.user-token";
const acceptanceSignedOutKey = "lodestone.acceptance.signed-out";
type AcceptanceAuthState = {
  token: string | null;
  setToken: Dispatch<SetStateAction<string | null>>;
};
const AcceptanceAuthContext = createContext<AcceptanceAuthState | undefined>(
  undefined,
);
const AcceptanceAuthSessionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [token, setToken] = useState(() =>
    sessionStorage.getItem(acceptanceTokenKey),
  );
  const value = useMemo(() => ({ token, setToken }), [token]);

  return (
    <AcceptanceAuthContext.Provider value={value}>
      {children}
    </AcceptanceAuthContext.Provider>
  );
};
const useAcceptanceAuthSession = (): {
  session: AuthSession;
  isAuthenticated: boolean;
  isLoading: boolean;
} => {
  const auth = useContext(AcceptanceAuthContext);
  if (!auth) {
    throw new Error("Acceptance auth session must be used within its provider");
  }
  const { token, setToken } = auth;
  const session = useMemo<AuthSession>(
    () => ({
      signIn: () => {
        if (sessionStorage.getItem(acceptanceSignedOutKey) === "true") {
          return Promise.resolve();
        }
        const acceptanceToken = "acceptance-shell-token";
        sessionStorage.setItem(acceptanceTokenKey, acceptanceToken);
        setToken(acceptanceToken);
        return Promise.resolve();
      },
      signOut: () => {
        sessionStorage.removeItem(acceptanceTokenKey);
        sessionStorage.setItem(acceptanceSignedOutKey, "true");
        setToken(null);
        return Promise.resolve();
      },
      getActor: () =>
        Promise.resolve(
          token
            ? { actorId: "acceptance-user", displayName: "Acceptance user" }
            : undefined,
        ),
      listTenantContexts: () =>
        Promise.resolve([{ organizationId, displayName: "Hungry Lion" }]),
      selectTenant: () => Promise.resolve(),
      getApiAccessToken: () =>
        token
          ? Promise.resolve(token)
          : Promise.reject(new Error("Acceptance token is missing")),
    }),
    [setToken, token],
  );
  return { session, isAuthenticated: Boolean(token), isLoading: false };
};
const useAcceptanceAuthSessionCallback = (
  onComplete: () => void,
): AuthSessionCallbackState => {
  useEffect(onComplete, [onComplete]);
  return { isLoading: false };
};

const acceptanceAuth =
  import.meta.env.VITE_LODESTONE_ACCEPTANCE_AUTH === "true";
export const AuthSessionProvider = acceptanceAuth
  ? AcceptanceAuthSessionProvider
  : LogtoAuthSessionProvider;
export const useAuthSession = acceptanceAuth
  ? useAcceptanceAuthSession
  : useLogtoAuthSession;
export const useAuthSessionCallback = acceptanceAuth
  ? useAcceptanceAuthSessionCallback
  : useLogtoAuthSessionCallback;
